import { Image, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import type { LeaderboardRowV1 } from '@/lib/leaderboard';

type SortBy = 'strength' | 'duelsWon' | 'volume';

type Props = {
  rows: LeaderboardRowV1[];
  onPressRow?: (row: LeaderboardRowV1) => void;
  showValue?: SortBy;
};

const formatScore = (value: number | null) => {
  if (value == null || !isFinite(value)) return '—';
  return `${Math.round(value)}`;
};

const formatWL = (wins: number | null, losses: number | null) => {
  const w = typeof wins === 'number' ? wins : 0;
  const l = typeof losses === 'number' ? losses : 0;
  return `${w}W-${l}L`;
};

const formatVolume = (value: number | null) => {
  if (value == null || !isFinite(value)) return '—';
  if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
  return `${Math.round(value)}`;
};

const getDisplayValue = (row: LeaderboardRowV1, showValue: SortBy): string => {
  switch (showValue) {
    case 'duelsWon':
      return formatWL(row.wins, row.losses);
    case 'volume':
      return formatVolume(row.total_volume);
    case 'strength':
    default:
      return formatScore(row.strength_score);
  }
};

const getDisplayLabel = (showValue: SortBy, units: string): string => {
  switch (showValue) {
    case 'duelsWon': return '';
    case 'volume': return units;
    case 'strength':
    default: return '';
  }
};

export default function LeaderboardList({ rows, onPressRow, showValue = 'strength' }: Props) {
  const { theme } = useTheme();
  const { profile } = useAuth();
  const units = profile?.units ?? 'lbs';

  return (
    <View style={styles.list}>
      {rows.map((row, index) => {
        const name = row.display_name || row.handle || 'Member';
        const subtitle = row.handle ? `@${row.handle}` : '';
        return (
          <TouchableOpacity
            key={row.user_id}
            activeOpacity={0.7}
            onPress={onPressRow ? () => onPressRow(row) : undefined}
            style={[
              styles.row,
              { backgroundColor: theme.colors.surface, borderColor: theme.colors.border },
            ]}
          >
            <Text style={[styles.rank, { color: theme.colors.textSecondary }]}>
              {index + 1}
            </Text>
            <View style={styles.avatarWrap}>
              {row.avatar_url ? (
                <Image source={{ uri: row.avatar_url }} style={styles.avatar} />
              ) : (
                <View style={[styles.avatarFallback, { backgroundColor: theme.colors.accent }]}>
                  <Text style={styles.avatarInitials}>
                    {(name || 'M').charAt(0).toUpperCase()}
                  </Text>
                </View>
              )}
            </View>
            <View style={styles.info}>
              <Text style={[styles.title, { color: theme.colors.text }]} numberOfLines={1}>
                {name}
              </Text>
              {subtitle ? (
                <Text
                  style={[styles.subtitle, { color: theme.colors.textSecondary }]}
                  numberOfLines={1}
                >
                  {subtitle}
                </Text>
              ) : null}
              {row.gym_name ? (
                <Text style={[styles.gymName, { color: theme.colors.textTertiary }]} numberOfLines={1}>
                  {row.gym_name}
                </Text>
              ) : null}
            </View>
            <View style={styles.meta}>
              <Text style={[styles.score, { color: theme.colors.text }]}>
                {getDisplayValue(row, showValue)}
              </Text>
              <Text style={[styles.wl, { color: theme.colors.textSecondary }]}>
                {getDisplayLabel(showValue, units)}
              </Text>
            </View>
          </TouchableOpacity>
        );
      })}
    </View>
  );
}

const styles = StyleSheet.create({
  list: {
    gap: 10,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 2,
    borderWidth: 1,
    padding: 12,
    gap: 10,
  },
  rank: {
    width: 32,
    fontSize: 16,
    fontWeight: '700',
    textAlign: 'center',
  },
  avatarWrap: {
    width: 40,
    height: 40,
    borderRadius: 4,
    overflow: 'hidden',
  },
  avatar: {
    width: 40,
    height: 40,
  },
  avatarFallback: {
    width: 40,
    height: 40,
    borderRadius: 4,
    justifyContent: 'center',
    alignItems: 'center',
  },
  avatarInitials: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '800',
  },
  info: {
    flex: 1,
    gap: 2,
  },
  title: {
    fontSize: 15,
    fontWeight: '700',
  },
  subtitle: {
    fontSize: 12,
    fontWeight: '600',
  },
  gymName: {
    fontSize: 11,
    fontWeight: '500',
    marginTop: 1,
  },
  meta: {
    alignItems: 'flex-end',
    gap: 4,
  },
  score: {
    fontSize: 16,
    fontWeight: '800',
  },
  wl: {
    fontSize: 12,
    fontWeight: '600',
  },
});
